import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';
import { getPopularCitiesList as getPopularCitiesList } from './zoocasa-light/helpers';
import {
  useZoocasaLightHomepageSections,
  type locationParams as HomePageLocationParams,
} from './zoocasa-light/use-zoocasa-light-homepage-sections';
import Link from 'components/link';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import useQueryParams from 'hooks/use-query-params';
import { useModalContext } from 'contexts/modal';
import Button from 'components/control/button';
import { buildClassName } from 'utils/build-class-name';
import styles from '../style.module.scss';
import lightStyles from './homepage-zoocasa-light.module.scss';
import DEFAULT_LISTING_PARAMS from 'contexts/preferences/listing-params/defaults';

import { useUserContext, usePreferencesContext } from 'contexts';
import { CountryCodeList, getCountryName } from 'types/countries';
import { homepageIds } from 'constants/test-constants';
import { generateSlugAndCoordinatesFromLastSearch, generateUrlsFromLastSearch, checkAreaPageExists, createSlug } from 'data/search-predictions';
import { GTM_CLICK_HOMEPAGE_SOLD_BUTTON,
  GTM_CLICK_HOMEPAGE_FEATURED_BUTTON, GTM_CLICK_HOMEPAGE_BUYERS_CARD_CLICK, GTM_CLICK_HOMEPAGE_BUYERS_BUTTON_CLICK,
  GTM_CLICK_HOMEPAGE_SELLERS_CARD_CLICK, GTM_CLICK_HOMEPAGE_SELLERS_BUTTON_CLICK,
  GTM_CLICK_HOMEPAGE_POPULAR_CITY_SELECT, GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON } from 'constants/events';
import { ModalOpeners } from 'types/modal';
import replaceFrenchAccents from 'utils/replace-french-accents';
import dynamic from 'next/dynamic';
import { useOnListingCardClick } from 'utils/listing-card';

import ZoocasaLightForSaleSection from './zoocasa-light/zoocasa-light-for-sale-section';
import CitySelector from '../local-focus/city-selector';
import { DEFAULT_CA_MARKETS, DEFAULT_US_MARKETS, type TopMarket } from '../local-focus/top-markets';
import { capitalizeLocation } from '@zoocasa/node-kit/strings/capitalize';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import InternalLinksContainer from '../internal-links/internalLinksContainer';
import { useIsTablet } from 'hooks/use-size-class';
import GlobeHeroSection from '../hero-section';

import type { HomePageProps } from '../index';
import { getTenantById } from '@tenants';
import ContentBlock from '../content-block';
import ListingCardCarousel from 'components/listing-card-carousel';
import LoadingText from 'components/loading-text';
import { CTA_CARD_TYPE_FEATURED, CTA_CARD_TYPE_SOLD } from '../home-modules/cta-card';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_VIEW_HOME_PAGE } from 'constants/events';

const NotificationBanner = dynamic(() => import('components/notification-banner'), {
  loading: () => <></>,
  ssr: false,
});

const ZoocasaCanadaBlogSection = dynamic(() => import('./zoocasa-canada-blog-section'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const KeywordsList = dynamic(() => import('../keywords-list'), {
  loading: () => <LoadingText />,
  ssr: true,
});

const Maintenance = dynamic(() => import('components/maintenance'), {
  loading: () => <></>,
  ssr: false,
});


export default function HomePageZoocasaLight({ isCrawler, isNotificationBannerHidden, availableHomeListings, availableHomeListingsMeta, availableHomeListingsLocation, isExpandedArea, internalLinksData, maintenance, routeName, headerData, tenantId, soldHomeListings, featuredHomeListings, homePageUrls }: HomePageProps) {
  const { t } = useTranslation(Namespaces.HOME);

  // Redirect URL for featured homes view more buttons. Note: the featured homes section only shows for Canadian site.
  const FEATURED_HOMES_REDIRECT_URL = '/ca-real-estate/houses';
  const tenant = getTenantById(tenantId);
  const theme = tenant.theme;
  const { userLocation, siteLocation, setIsOnSignUpUrl, isAuthenticated } = useUserContext();
  const { lastSearchLocation } = usePreferencesContext();
  const { openModal } = useModalContext();
  const {
    homesForSaleUrl: initialHomesForSaleUrl,
    mapViewUrl: initialMapViewUrl,
    soldPricesUrl: initialSoldPricesUrl,
  } = homePageUrls;
  const pageRef = useRef(null);
  // Seed the slug from the site's country so SSR / no-JS crawlers render a sold-prices link
  // for the correct country. Without this, a US site would emit the CA default ("toronto-on")
  // until the client-side location effect runs — leaking a Canadian link on the US homepage.
  const [currentSlug, setCurrentSlug] = useState(
    siteLocation === CountryCodeList.UNITED_STATES ? defaultUSCityPayload.slug : DEFAULT_LISTING_PARAMS.filter.slug,
  );
  const [currentAreaName, setCurrentAreaName] = useState(DEFAULT_LISTING_PARAMS.filter.areaName);
  const [currentLatitude, setCurrentLatitude] = useState(DEFAULT_LISTING_PARAMS.filter.latitude);
  const [currentLongitude, setCurrentLongitude] = useState(DEFAULT_LISTING_PARAMS.filter.longitude);
  const [currentSublocation, setCurrentSublocation] = useState('');
  const [lastSearchCityUrl, setLastSearchCityUrl] = useState<string | null>(() =>
    lastSearchLocation ? initialHomesForSaleUrl : null
  );
  const isCanadianSite = siteLocation === CountryCodeList.CANADA;
  const popularCities = isCanadianSite ? DEFAULT_CA_MARKETS : DEFAULT_US_MARKETS;
  const defaultCitySelected = useMemo<TopMarket>(
    () =>
      isCanadianSite
        ? (DEFAULT_CA_MARKETS.find(m => m.slug === defaultCACityPayload.slug) ?? {
          name: defaultCACityPayload.name,
          slug: defaultCACityPayload.slug,
        })
        : { name: defaultUSCityPayload.name, slug: defaultUSCityPayload.slug },
    [isCanadianSite]
  );
  const isTablet = useIsTablet();
  const onListingCardClick = useOnListingCardClick({ isHomePage: true });

  // Set Home Page location parameters after the popular city selected from dropdown.
  const setHomePageLocationParams = useCallback((payload: HomePageLocationParams) => {
    setCurrentSlug(payload.slug);
    setCurrentAreaName(payload.areaName);
    setCurrentLatitude(payload.latitude);
    setCurrentLongitude(payload.longitude);
    setCurrentSublocation(payload.subLocation);
  }, []);

  const {
    applyPopularCityChange,
    onPopularCityChange,
    homesForSaleUrl: forSaleSectionHomesUrl,
    mapViewUrl: forSaleSectionMapUrl,
    soldPricesUrl,
    displayedForSaleListings,
    displayedForSaleListingCount,
    displayedForSaleIsExpandedArea,
    soldListingsForSection,
    featuredListingsForSection,
  } = useZoocasaLightHomepageSections({
    tenantThemeId: theme.id,
    isCanadianSite,
    initialHomesForSaleUrl,
    initialMapViewUrl,
    initialSoldPricesUrl,
    availableHomeListings,
    availableHomeListingsMeta,
    availableHomeListingsLocation,
    isExpandedArea,
    currentSlug,
    currentAreaName,
    currentLatitude,
    currentLongitude,
    soldHomeListings,
    featuredHomeListings,
    setHomePageLocationParams,
  });

  const generateCoordinatesAndSlug = async () => {
    if (lastSearchLocation) {
      const [slugResponse, urlResponse] = await Promise.all([
        generateSlugAndCoordinatesFromLastSearch(lastSearchLocation),
        generateUrlsFromLastSearch(lastSearchLocation),
      ]);
      setCurrentSlug(slugResponse?.slug || currentSlug);
      setCurrentAreaName(lastSearchLocation.description);
      setCurrentLatitude(slugResponse?.latitude || currentLatitude);
      setCurrentLongitude(slugResponse?.longitude || currentLongitude);
      setCurrentSublocation(slugResponse?.subLocation);

      // generateUrlsFromLastSearch only resolves area pages for our go-search predictions. For Google predictions we need to use the same logic as the search bar ie. 
      // we need to check if the area page exists. If it doesn't fall back to search page.
      let resolvedUrl = urlResponse?.homesForSaleUrl ?? null;
      if (resolvedUrl === '/search' || resolvedUrl === null) {
        const derivedSlug = createSlug(lastSearchLocation);
        if (derivedSlug && await checkAreaPageExists(derivedSlug)) {
          resolvedUrl = `/${derivedSlug}-real-estate/filter?has-image=false`;
          setCurrentSlug(derivedSlug);
        } else {
          resolvedUrl = urlResponse?.mapViewUrl ?? '/search';
        }
      }
      setLastSearchCityUrl(resolvedUrl);
      return;
    }

    if (userLocation && userLocation.countryCode === 'US') {
      setCurrentSlug(userLocation.slug);
      setCurrentAreaName(userLocation.name);
      setCurrentLatitude(userLocation.latitude);
      setCurrentLongitude(userLocation.longitude);
      setCurrentSublocation('');
      return;
    }

    setCurrentSlug(isCanadianSite ? defaultCACityPayload.slug : defaultUSCityPayload.slug);
    setCurrentAreaName(isCanadianSite ? defaultCACityPayload.name : defaultUSCityPayload.name);
    setCurrentLatitude(isCanadianSite ? defaultCACityPayload.latitude : defaultUSCityPayload.latitude);
    setCurrentLongitude(isCanadianSite ? defaultCACityPayload.longitude : defaultUSCityPayload.longitude);
    setCurrentSublocation('');

    applyPopularCityChange(defaultCitySelected);
  };

  useQueryParams(params => openModal('reset-password', params), ModalOpeners.RESET_PASSWORD_TOKEN, ModalOpeners.EMAIL);
  useQueryParams(params => openModal('confirm-account', params), ModalOpeners.CONFIRMATION_TOKEN, ModalOpeners.USER_AGENT);
  useQueryParams(params => openModal('login-registration', params), ModalOpeners.SIGNIN_EMAIL);
  useQueryParams(params => openModal('login-registration', params), ModalOpeners.WARNING_MESSAGE);
  useQueryParams(params => openModal('notification', { message: params.notification as string }), ModalOpeners.NOTIFICATION);

  /* eslint-disable react-hooks/set-state-in-effect -- these mount/last-search effects intentionally
     seed location, slug and URL state on the client after bootstrap; the async location resolution
     can't run during render. */
  useEffect(() => {
    const isLoginUrl = window.location.href.endsWith('#sign-up');
    if (isLoginUrl && !isAuthenticated) {
      setIsOnSignUpUrl(true);
      openModal('login-registration');
      // Trigger a click event on the screen otherwise modal will not open
      setTimeout(() => {
        pageRef.current?.click();
      }, 0);
    }
    else {
      setIsOnSignUpUrl(false);
    }

    generateCoordinatesAndSlug();
    AnalyticsController.trackEvent(GTM_VIEW_HOME_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastSearchLocation) {
      setLastSearchCityUrl(initialHomesForSaleUrl);
    } else {
      setLastSearchCityUrl(null);
    }
    generateCoordinatesAndSlug();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSearchLocation, initialHomesForSaleUrl, initialMapViewUrl, initialSoldPricesUrl]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Use last searched location/preference first if available, otherwise use the popular city options.
  const popularCityLabel = useMemo(
    () => {
      if (lastSearchLocation) {
        return capitalizeLocation(lastSearchLocation.description);
      }
      return popularCities.find(m => m.slug === currentSlug)?.name ??
        capitalizeLocation(
          (currentAreaName && currentAreaName.trim()) || availableHomeListingsLocation
        );
    },
    [currentSlug, currentAreaName, availableHomeListingsLocation, popularCities, lastSearchLocation]
  );

  function handlePopularCitySelect(market: TopMarket) {
    AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_POPULAR_CITY_SELECT, 'All', market.name);
    onPopularCityChange(market);
  }

  function handleLastSearchCityClick() {
    AnalyticsController.trackEvent(GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON);
  }

  /** Build popular cities list for the city selector dropdown. If current city (user's last search) is not in popular cities list we append it. */
  const popularCitiesListSelector = useMemo(
    () =>
      getPopularCitiesList(
        popularCities,
        currentSlug,
        popularCityLabel,
        siteLocation ?? CountryCodeList.CANADA
      ),
    [popularCities, currentSlug, popularCityLabel, siteLocation]
  );

  const citySelectorActiveName = useMemo(
    () => (currentSlug.trim() === '' ? getCountryName(siteLocation) : popularCityLabel),
    [currentSlug, siteLocation, popularCityLabel]
  );

  //Disable popular city selector if user has last searched location/ preference
  const citySelectorDisabled = useMemo(
    () =>
      Boolean(lastSearchLocation || (userLocation && userLocation.countryCode === 'US')),
    [lastSearchLocation, userLocation]
  );

  const getTranslatedSectionTitle = useMemo(
    () =>
      displayedForSaleIsExpandedArea
        ? t(LOCALE_HOME_PATHS.FOR_SALE_SECTION_LISTINGS_NEAR, {
          count: displayedForSaleListingCount,
          defaultValue: '{{count}}+ Listings near',
        })
        : t(LOCALE_HOME_PATHS.FOR_SALE_SECTION_LISTINGS_IN, {
          count: displayedForSaleListingCount,
          defaultValue: '{{count}}+ Listings in',
        }),
    [displayedForSaleIsExpandedArea, displayedForSaleListingCount, t]
  );

  return (
    <>
      <HeadData data={headerData} />
      <Layout routeName={routeName} showMobileBottomNav={false}>
        <NotificationBanner label="New Construction Homes are here!" link="/newhomes" isHidden={isNotificationBannerHidden} />
        <GlobeHeroSection />
      
        <section className={buildClassName(styles.component, lightStyles['zoocasa-light'])} ref={pageRef}>
          <div className={buildClassName(styles['home-modules'], styles.top)}>
            <ZoocasaLightForSaleSection
              className={lightStyles['section-for-sale']}
              titleClassName={lightStyles['for-sale-title']}
              isCrawler={isCrawler}
              listings={displayedForSaleListings}
              homesForSaleUrl={lastSearchCityUrl ?? forSaleSectionHomesUrl}
              mapViewUrl={forSaleSectionMapUrl}
              onListingCardClick={onListingCardClick}
              sectionTitle={getTranslatedSectionTitle}
              citySelector={
                <CitySelector
                  activeCityName={citySelectorActiveName}
                  markets={popularCitiesListSelector}
                  activeSlug={currentSlug}
                  onCityChange={handlePopularCitySelect}
                  onLastSearchCityClick={handleLastSearchCityClick}
                  dropdownLayout="grid"
                  dropdownDisabled={citySelectorDisabled}
                  lastSearchedCityUrl={replaceFrenchAccents(lastSearchCityUrl ?? forSaleSectionHomesUrl)}
                />
              }
            />
            {isCanadianSite && <ZoocasaCanadaBlogSection className={lightStyles['section-blog']} />}
            {soldListingsForSection.length > 0 && <section className={buildClassName(styles['home-sections'], styles.sold, lightStyles['section-sold'])}>
              <div className={styles['home-block']} data-testid={homepageIds.soldPricesWidget}>
                <div className={styles.row}>
                  <Link href={replaceFrenchAccents(soldPricesUrl)} testId={homepageIds.soldPricesTitle} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)}>
                    <h2>Sold Prices</h2>
                    <p className={styles.label}>Search Sold Data from 2003 - {new Date().getFullYear()}</p>
                  </Link>
                  { !isTablet &&
                        <Link href={replaceFrenchAccents(soldPricesUrl)}>
                          <Button label="View Sold Properties" theme="secondary" expHomePadding={false} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)} testId={homepageIds.soldPricesMoreDataButton} />
                        </Link>
                  }
                </div>
                <ListingCardCarousel
                  key={`sold-${currentSlug}`}
                  listingCardsData={{ listings: soldListingsForSection, ctaType: CTA_CARD_TYPE_SOLD, userLocation, redirectUrl: replaceFrenchAccents(soldPricesUrl) }}
                  isCrawler={isCrawler}
                  slidesPerGroupAuto
                  onListingCardClick={onListingCardClick}
                />
                { isTablet &&
                      <Link href={replaceFrenchAccents(soldPricesUrl)}>
                        <Button className={buildClassName(styles['tablet-button'], lightStyles['homepage-btn'])} label="View Sold Properties" theme="secondary" expHomePadding={false} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)} testId={homepageIds.soldPricesMoreDataButton} />
                      </Link>
                }
              </div>
            </section>}
          </div>
          <div className={buildClassName(styles['home-modules'], styles.bottom)}>
            {isCanadianSite && featuredListingsForSection.length > 0 &&
              <section className={buildClassName(styles['home-sections'], styles['featured-homes'], lightStyles['section-featured'])}>
                <div className={styles['home-block']} data-testid={homepageIds.featuredHomesWidget}>
                  <div className={styles.row}>
                    <Link href={FEATURED_HOMES_REDIRECT_URL} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}>
                      <h2 data-testid={homepageIds.featuredHomesTitle}>Featured Homes</h2>
                    </Link>
                    { !isTablet &&
                          <Link href={FEATURED_HOMES_REDIRECT_URL}>
                            <Button label="View More" theme="secondary" testId={homepageIds.featuredHomesMoreListingsButton} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}/>
                          </Link>
                    }
                  </div>
                  <ListingCardCarousel
                    key="featured"
                    listingCardsData={{ listings: featuredListingsForSection, ctaType: CTA_CARD_TYPE_FEATURED, userLocation, redirectUrl: FEATURED_HOMES_REDIRECT_URL }}
                    isCrawler={isCrawler}
                    slidesPerGroupAuto
                    onListingCardClick={onListingCardClick}
                  />
                  { isTablet &&
                        <Link href={FEATURED_HOMES_REDIRECT_URL}>
                          <Button className={buildClassName(styles['tablet-button'], lightStyles['homepage-btn'])} label="View More" theme="secondary" testId={homepageIds.featuredHomesMoreListingsButton} onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}/>
                        </Link>
                  }
                </div>
              </section>
            }
            <div>
              <section className={buildClassName(styles['home-sections'], styles['content-block'], styles['buy-guide'], lightStyles['section-content-block'])}>
                <div className={styles['home-block']}>
                  <ContentBlock content={theme.contentBlockBuy} cardEvent={GTM_CLICK_HOMEPAGE_BUYERS_CARD_CLICK} buttonEvent={GTM_CLICK_HOMEPAGE_BUYERS_BUTTON_CLICK} />
                </div>
              </section>
              <section className={buildClassName(styles['home-sections'], styles['content-block'], styles['buy-guide'], lightStyles['section-content-block'])}>
                <div className={styles['home-block']}>
                  <ContentBlock content={theme.contentBlockSell} cardEvent={GTM_CLICK_HOMEPAGE_SELLERS_CARD_CLICK} buttonEvent={GTM_CLICK_HOMEPAGE_SELLERS_BUTTON_CLICK} right={true} />
                </div>
              </section>
            </div>
          </div>
          {isCanadianSite &&
            <section className={buildClassName(styles['home-sections'], styles['find-by-keyword'], lightStyles['section-keywords'])} data-testid={homepageIds.searchByKeywordsWidget}>
              <div className={styles.container}>
                <div className={styles['home-block']}>
                  <div className={buildClassName(styles.row, styles['find-by-keyword-heading'])} data-testid={homepageIds.searchByKeywordsTitle}>
                    <h2>Search by Keywords</h2>
                  </div>
                  <div>
                    <KeywordsList slug={currentSlug} areaName={currentAreaName} latitude={currentLatitude} longitude={currentLongitude} subLocation={currentSublocation} />
                  </div>
                </div>
              </div>
            </section>
          }
        </section>



        <InternalLinksContainer data={internalLinksData} />
        {maintenance.isMaintenance && <Maintenance maintenance={maintenance} />}
      </Layout>
    </>
  );
}
